import { 
  addDays, 
  addWeeks, 
  addMonths, 
  addYears, 
  isBefore, 
  parseISO, 
  formatISO, 
  getDay, 
  setDate as setDateFns
} from 'date-fns';
import type { IActivity } from '../types/activity';

type DateLike = Date | string | number;

/**
 * Generates recurring instances of an activity based on its recurrence rules
 * @param activity The base activity to generate instances for
 * @param until Optional end date to generate instances until
 * @returns Array of activity instances
 */
export function generateRecurringInstances(
  activity: IActivity,
  until?: DateLike
): IActivity[] {
  if (!activity.is_recurring || !activity.recurrence_rule || activity.recurrence_rule === 'NONE') {
    return [];
  }

  const startDate = typeof activity.start_date === 'string' 
    ? parseISO(activity.start_date) 
    : new Date(activity.start_date);
  
  const endDate = activity.recurrence_end_date 
    ? (typeof activity.recurrence_end_date === 'string' 
        ? parseISO(activity.recurrence_end_date) 
        : new Date(activity.recurrence_end_date))
    : until 
      ? (typeof until === 'string' ? parseISO(until) : new Date(until))
      : addYears(startDate, 1); // Default to 1 year ahead if no end date

  const instances: IActivity[] = [];
  const interval = activity.recurrence_interval || 1;
  let currentDate = startDate;
  let instanceCount = 0;
  const maxInstances = 100; // Safety limit

  while (isBefore(currentDate, endDate) && instanceCount < maxInstances) {
    // Skip the first instance (it's the original)
    if (instanceCount > 0) {
      const instance = {
        ...activity,
        id: 0, // Will be set by the database
        parent_id: activity.id,
        start_date: formatISO(currentDate),
        end_date: formatISO(
          new Date(currentDate.getTime() + (new Date(activity.end_date).getTime() - startDate.getTime()))
        ),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      instances.push(instance);
    }

    // Calculate next occurrence
    switch (activity.recurrence_rule) {
      case 'DAILY':
        currentDate = addDays(currentDate, interval);
        break;
      
      case 'WEEKLY':
        if (activity.recurrence_days?.length) {
          // Find next day in the week that matches recurrence_days
          const currentDay = getDay(currentDate);
          const sortedDays = [...activity.recurrence_days].sort((a, b) => a - b);
          const nextDay = sortedDays.find(day => day > currentDay) ?? sortedDays[0];
          
          if (nextDay !== undefined) {
            // Calculate days to add based on whether we're in the same week or next
            const daysToAdd = nextDay > currentDay 
              ? nextDay - currentDay 
              : 7 - currentDay + nextDay;
              
            currentDate = addDays(currentDate, daysToAdd);
            
            // If we've wrapped around to the next week and interval > 1, add the remaining weeks
            if (nextDay <= currentDay && interval > 1) {
              currentDate = addWeeks(currentDate, interval - 1);
            }
          } else {
            // Fallback to weekly interval if no valid next day found
            currentDate = addWeeks(currentDate, interval);
          }
        } else {
          currentDate = addWeeks(currentDate, interval);
        }
        break;
      
      case 'MONTHLY':
        if (activity.recurrence_month_day) {
          // Specific day of month (e.g., 15th of each month)
          // First move to the next month
          currentDate = addMonths(currentDate, interval);
          // Then set to the specific day of month, handling months with fewer days
          const lastDayOfMonth = new Date(
            currentDate.getFullYear(), 
            currentDate.getMonth() + 1, 
            0
          ).getDate();
          const dayToSet = Math.min(activity.recurrence_month_day, lastDayOfMonth);
          currentDate = setDateFns(currentDate, dayToSet);
        } else if (activity.recurrence_weekday !== undefined && activity.recurrence_weekday_ordinal) {
          // Specific weekday (e.g., second Wednesday of each month)
          currentDate = addMonths(currentDate, interval);
          // Set to first day of month
          currentDate = setDateFns(currentDate, 1);
          // Find the nth occurrence of the weekday
          let occurrences = 0;
          const targetWeekday = activity.recurrence_weekday;
          const targetOrdinal = activity.recurrence_weekday_ordinal;
          
          while (occurrences < targetOrdinal && currentDate.getMonth() === addMonths(currentDate, -1).getMonth()) {
            if (getDay(currentDate) === targetWeekday) {
              occurrences++;
              if (occurrences === targetOrdinal) break;
            }
            if (occurrences < targetOrdinal) {
              currentDate = addDays(currentDate, 1);
            }
          }
        } else {
          // Default to same day of month
          currentDate = addMonths(currentDate, interval);
        }
        break;
      
      case 'YEARLY':
        currentDate = addYears(currentDate, interval);
        break;
      
      case 'CUSTOM':
      default:
        // For custom recurrence, the frontend should handle the logic
        // and provide the exact dates
        currentDate = addDays(currentDate, 1); // Default to daily if not specified
        break;
    }

    instanceCount++;
  }

  return instances;
}

/**
 * Gets the base activity for a recurring series
 */
export function getBaseActivity(activity: IActivity, activities: IActivity[]): IActivity | null {
  if (!activity.parent_id) return activity;
  return activities.find(a => a.id === activity.parent_id) || activity;
}

/**
 * Gets all instances of a recurring activity
 */
export function getRecurringInstances(activity: IActivity, activities: IActivity[]): IActivity[] {
  if (!activity.is_recurring) return [activity];
  
  const baseId = activity.parent_id || activity.id;
  return activities.filter(a => 
    a.id === baseId || 
    a.parent_id === baseId ||
    (a.is_recurring && a.id === activity.id)
  );
}

/**
 * Gets the next occurrence of a recurring activity
 */
export function getNextOccurrence(activity: IActivity, activities: IActivity[]): IActivity | null {
  if (!activity.is_recurring) return null;
  
  const now = new Date();
  const instances = getRecurringInstances(activity, activities)
    .filter(a => new Date(a.start_date) > now)
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
  
  return instances[0] || null;
}
